import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

/**
 * Template 14: JHAROKHA Header
 * Clean white header, teal logo bar, structured nav
 * Mobile: teal slide-in menu
 */
const Header14 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template14' },
        { label: 'Shop', path: '/template14/shop' },
        { label: 'About', path: '/template14/about' },
        { label: 'Contact', path: '/template14/contact' },
        { label: 'Orders', path: '/template14/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&family=Roboto:wght@300;400;500&display=swap');
                .jharokha-desktop-nav { display: none; }
                .jharokha-mobile-toggle { display: flex; }
                .jharokha-desktop-actions { display: none; }
                .jharokha-mobile-actions { display: flex; }
                @media (min-width: 1024px) {
                    .jharokha-desktop-nav { display: flex; }
                    .jharokha-mobile-toggle { display: none; }
                    .jharokha-desktop-actions { display: flex; }
                    .jharokha-mobile-actions { display: none; }
                }
            `}</style>

            <header
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: '#FFFFFF',
                    borderBottom: scrolled ? '1px solid #E5E7EB' : '1px solid transparent',
                    boxShadow: scrolled ? '0 1px 8px rgba(0,0,0,0.06)' : 'none',
                    transition: 'all 0.3s ease',
                }}
            >
                {/* Teal Top Bar */}
                <div
                    style={{
                        backgroundColor: '#0D9488',
                        padding: '6px 0',
                        textAlign: 'center',
                    }}
                >
                    <p
                        style={{
                            fontFamily: "'Roboto', sans-serif",
                            fontSize: '11px',
                            color: '#FFFFFF',
                            letterSpacing: '0.15em',
                            fontWeight: 400,
                            margin: 0,
                        }}
                    >
                        FREE SHIPPING ON ORDERS ABOVE ₹4,999
                    </p>
                </div>

                {/* Main Header */}
                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 20px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '68px',
                    }}
                >
                    {/* Left: Mobile Menu Toggle */}
                    <button
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="jharokha-mobile-toggle"
                        style={{
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '40px',
                            height: '40px',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#1A1A1A',
                        }}
                    >
                        {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>

                    {/* Brand */}
                    <h1
                        onClick={() => navigate('/template14')}
                        style={{
                            fontFamily: "'Archivo', sans-serif",
                            fontSize: '22px',
                            fontWeight: 700,
                            color: '#0D9488',
                            cursor: 'pointer',
                            letterSpacing: '3px',
                            margin: 0,
                            textTransform: 'uppercase',
                        }}
                    >
                        JHAROKHA
                    </h1>

                    {/* Center: Nav Links (Desktop) */}
                    <nav
                        className="jharokha-desktop-nav"
                        style={{
                            alignItems: 'center',
                            gap: '32px',
                        }}
                    >
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                style={{
                                    fontFamily: "'Archivo', sans-serif",
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    color: '#1A1A1A',
                                    letterSpacing: '0.1em',
                                    textTransform: 'uppercase',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px 0',
                                    position: 'relative',
                                    transition: 'color 0.3s ease',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.color = '#0D9488'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.color = '#1A1A1A'; }}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right: Actions (Desktop) */}
                    <div
                        className="jharokha-desktop-actions"
                        style={{ alignItems: 'center', gap: '20px' }}
                    >
                        <button
                            onClick={() => navigate('/template14/shop')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A1A1A', transition: 'color 0.3s' }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#0D9488'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#1A1A1A'; }}
                        >
                            <Search className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => navigate('/template14/profile')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A1A1A', transition: 'color 0.3s' }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#0D9488'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#1A1A1A'; }}
                        >
                            <User className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => navigate('/template14/cart')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A1A1A', position: 'relative', transition: 'color 0.3s' }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#0D9488'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = '#1A1A1A'; }}
                        >
                            <ShoppingCart className="w-5 h-5" />
                            {cartCount > 0 && (
                                <span style={{ position: 'absolute', top: '-6px', right: '-8px', backgroundColor: '#0D9488', color: '#FFFFFF', fontSize: '10px', fontWeight: 600, width: '18px', height: '18px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {cartCount}
                                </span>
                            )}
                        </button>
                    </div>

                    {/* Mobile: Cart Icon */}
                    <div className="jharokha-mobile-actions" style={{ alignItems: 'center', gap: '12px' }}>
                        <button
                            onClick={() => navigate('/template14/cart')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#1A1A1A', position: 'relative' }}
                        >
                            <ShoppingCart className="w-5 h-5" />
                            {cartCount > 0 && (
                                <span style={{ position: 'absolute', top: '-6px', right: '-8px', backgroundColor: '#0D9488', color: '#FFFFFF', fontSize: '10px', fontWeight: 600, width: '18px', height: '18px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {cartCount}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Slide-In Menu */}
            {mobileMenuOpen && (
                <div
                    style={{ position: 'fixed', inset: 0, zIndex: 40, backgroundColor: 'rgba(0,0,0,0.5)' }}
                    onClick={() => setMobileMenuOpen(false)}
                >
                    <div
                        style={{
                            position: 'absolute', top: 0, left: 0, bottom: 0,
                            width: '300px', maxWidth: '85vw',
                            backgroundColor: '#0D9488',
                            padding: '80px 0 40px',
                            overflowY: 'auto',
                            animation: 'jharokhaSlideIn 0.3s ease',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <style>{`
                            @keyframes jharokhaSlideIn {
                                from { transform: translateX(-100%); }
                                to { transform: translateX(0); }
                            }
                        `}</style>

                        {/* Brand in Mobile Menu */}
                        <div style={{ padding: '0 24px', marginBottom: '32px' }}>
                            <h2 style={{ fontFamily: "'Archivo', sans-serif", fontSize: '24px', fontWeight: 700, color: '#FFFFFF', letterSpacing: '3px', margin: 0 }}>
                                JHAROKHA
                            </h2>
                            <div style={{ width: '40px', height: '2px', backgroundColor: 'rgba(255,255,255,0.4)', marginTop: '12px' }} />
                        </div>

                        {/* Mobile Nav Links */}
                        <nav style={{ display: 'flex', flexDirection: 'column' }}>
                            {navLinks.map((link) => (
                                <button
                                    key={link.label}
                                    onClick={() => handleNavClick(link.path)}
                                    style={{
                                        fontFamily: "'Archivo', sans-serif", fontSize: '15px', fontWeight: 500,
                                        color: '#FFFFFF', letterSpacing: '0.1em', textTransform: 'uppercase',
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        padding: '14px 24px', textAlign: 'left',
                                        borderBottom: '1px solid rgba(255,255,255,0.1)',
                                        transition: 'background-color 0.2s',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                >
                                    {link.label}
                                </button>
                            ))}
                        </nav>

                        {/* Mobile Menu Footer */}
                        <div style={{ padding: '24px', marginTop: '24px' }}>
                            <button
                                onClick={() => handleNavClick('/template14/login')}
                                style={{
                                    fontFamily: "'Archivo', sans-serif", fontSize: '13px', fontWeight: 500,
                                    color: '#FFFFFF', background: 'none',
                                    border: '1px solid rgba(255,255,255,0.3)', cursor: 'pointer',
                                    padding: '12px 24px', width: '100%', textAlign: 'center',
                                    letterSpacing: '0.1em', textTransform: 'uppercase',
                                }}
                            >
                                Sign In
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header14;
